// Camera ID helpers — shared between the producer editor
// (CreateEditBriefing), the producer briefing view (BriefingView) and
// the crew app (mobile briefing tab + home).
//
// A "Camera ID" is the letter(s) a camera operator / photographer dials
// into their camera so the editor knows who shot the footage. It is
// 1–3 uppercase letters (e.g. "A", "JES"). The producer fills it in,
// and it must be UNIQUE PER DAY: on a single festival day no two crew
// members may carry the same Camera ID, but the same letter may recur
// on a different day of a multi-day production.
//
// Camera IDs only apply to camera / photo functions — editors,
// spotters, datahandlers, etc. are not relevant. The set below is the
// camera/photo subset of CREW_ROLES (see CreateEditBriefing.tsx).
//
// crew_members / briefing_crew_days both carry one row per (person,
// event_date), so a Camera ID lives naturally per-day on those rows.
// This module owns the role filter, validation, auto-assign and the
// per-day uniqueness check once, with tests, so the three call sites
// can't drift.

/**
 * The crew functions (matching CREW_ROLES in ./crewRoles) for which a
 * Camera ID is relevant — i.e. camera operators, photographers and drone
 * pilots. Editors / spotters / etc. are deliberately excluded.
 */
export const CAMERA_ID_ROLES = [
  "video general",
  "photo",
  "video aftermovie",
  "video recap",
  "video one-taker",
  "video artist snippets",
  "video interviews",
  "video livestream",
  "drone pilot",
  "FPV pilot",
  "cinematic drone pilot",
] as const;

export type CameraIdRole = (typeof CAMERA_ID_ROLES)[number];

// Keyed lowercase because isCameraIdRole lowercases its input and some
// canonical roles carry acronym casing (e.g. "FPV pilot").
const CAMERA_ID_ROLE_SET: ReadonlySet<string> = new Set(
  CAMERA_ID_ROLES.map((role) => role.toLowerCase()),
);

/** Max length of a Camera ID (e.g. "JES"). */
const CAMERA_ID_MAX_LENGTH = 3;

/**
 * True when the given crew function should get a Camera ID slot.
 * Case-insensitive and whitespace-tolerant so it survives the lenient
 * casing in stored `function` values.
 */
export function isCameraIdRole(fn: string | null | undefined): boolean {
  return CAMERA_ID_ROLE_SET.has((fn ?? "").trim().toLowerCase());
}

/**
 * Canonical storage / comparison form of a Camera ID: trimmed and
 * uppercased. Empty string means "not assigned".
 */
export function normalizeCameraId(value: string | null | undefined): string {
  return (value ?? "").trim().toUpperCase();
}

/**
 * A Camera ID is valid when empty (unassigned) or 1–3 letters A–Z.
 * Producers may choose a single letter or a 3-letter combo themselves.
 */
export function isValidCameraId(value: string | null | undefined): boolean {
  const v = normalizeCameraId(value);
  return v === "" || new RegExp(`^[A-Z]{1,${CAMERA_ID_MAX_LENGTH}}$`).test(v);
}

/**
 * Sanitize raw producer keystrokes into an allowed partial Camera ID:
 * strip non-letters, uppercase, cap at CAMERA_ID_MAX_LENGTH. Suitable
 * for an input's onChange so the field can never hold an invalid value.
 */
export function sanitizeCameraIdInput(value: string): string {
  return value
    .replace(/[^a-zA-Z]/g, "")
    .toUpperCase()
    .slice(0, CAMERA_ID_MAX_LENGTH);
}

/**
 * The Camera ID for a zero-based position in the alphabet, used by
 * auto-assign: 0→"A", 25→"Z", 26→"AA", … (bijective base-26). The
 * common case (≤26 crew per day) yields single letters A, B, C, …
 */
export function cameraIdForIndex(index: number): string {
  let n = Math.max(0, Math.floor(index));
  let out = "";
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

export type CameraIdEntry = { key: string; cameraId: string | null | undefined };

/**
 * Given the Camera ID entries for a SINGLE day, return the set of keys
 * whose Camera ID collides with another entry that day (case-insensitive,
 * empties ignored). Drives the per-day uniqueness validation in the UI.
 */
export function findDuplicateCameraIds(entries: CameraIdEntry[]): Set<string> {
  const keysByValue = new Map<string, string[]>();
  for (const entry of entries) {
    const value = normalizeCameraId(entry.cameraId);
    if (!value) continue;
    const bucket = keysByValue.get(value);
    if (bucket) bucket.push(entry.key);
    else keysByValue.set(value, [entry.key]);
  }
  const duplicates = new Set<string>();
  for (const keys of keysByValue.values()) {
    if (keys.length > 1) keys.forEach((k) => duplicates.add(k));
  }
  return duplicates;
}

export type CameraIdShift = { event_date: string; camera_id: string | null | undefined };

/**
 * Pick the Camera ID to surface for a single day from a person's
 * shifts — used by the crew home screen. Prefers the shift matching
 * `dateIso` (Camera IDs can differ per day), else the first shift that
 * carries one. Returns "" when the person has no Camera ID at all.
 */
export function selectCameraIdForDate(shifts: CameraIdShift[], dateIso: string): string {
  const withCam = shifts.filter((s) => normalizeCameraId(s.camera_id) !== "");
  if (withCam.length === 0) return "";
  const today = withCam.find((s) => s.event_date === dateIso);
  return normalizeCameraId((today ?? withCam[0]).camera_id);
}
